import { useEffect, useId, useState, type ReactNode } from 'react';
import { ChevronDownIcon } from '../icons';
import './DetailsDisclosure.css';

export interface DisclosureProps {
  /** Label when closed / open. */
  label: string;
  openLabel?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  /** Smaller button text. */
  size?: 's' | 'm';
}

/**
 * `<button aria-expanded>` + region, animated with `grid-template-rows 0fr→1fr`.
 * Content stays in the DOM (printable, searchable) but is inert when closed.
 */
export function Disclosure({ label, openLabel, open: controlled, defaultOpen = false, onToggle, children, className, size = 'm' }: DisclosureProps) {
  const [internal, setInternal] = useState(defaultOpen);
  const open = controlled ?? internal;
  const id = useId();
  useEffect(() => {
    if (controlled === undefined) setInternal(defaultOpen);
  }, [defaultOpen, controlled]);
  const toggle = () => {
    const next = !open;
    if (controlled === undefined) setInternal(next);
    onToggle?.(next);
  };
  return (
    <div className={['disclosure', `disclosure-${size}`, className ?? ''].filter(Boolean).join(' ')} data-open={open}>
      <button type="button" className="disclosure-button" aria-expanded={open} aria-controls={id} onClick={toggle}>
        <ChevronDownIcon size={16} className="disclosure-chevron" />
        <span>{open ? openLabel ?? label : label}</span>
      </button>
      <div id={id} role="region" className="disclosure-panel" aria-hidden={!open} inert={!open ? true : undefined}>
        <div className="disclosure-panel-inner">{children}</div>
      </div>
    </div>
  );
}
